import React, { useEffect, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Table, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getColumnsByType } from "./columns";
import { getNotAssignedPersons } from "../../../redux/slices/persons/personsThunk";
import { assignPersons } from "../../../redux/slices/delegates/delegatesThunk";
import { toast } from "react-toastify";
import { getGovernments } from "../../../redux/slices/governments/governmentsThunk";
import { getRegions } from "../../../redux/slices/regions/regionsThunk";
import Filter from "../../../components/filter/Filter";
import { getSpecialtysList } from "../../../redux/slices/specialtys/specialtysThunk";

const AssignPersonsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // --- Données du Store Redux ---
  const { persons: persons_data, loading: persons_loading } = useSelector((state) => state.persons);
  const { loading: assignment_loading } = useSelector((state) => state.delegates.assigned);
  const { governments: governmentsData, loading: government_loading } = useSelector((state) => state.governments);
  const { regions: regions_data, loading: region_loading } = useSelector((state) => state.regions);
  const { specialtys: sepcialtysData, loading: sepcialty_loading } = useSelector((state) => state.specialtys);

  // --- États locaux ---
  // On récupère l'ID du délégué de manière sécurisée
  const delegateId = useMemo(() => {
    const id = location.pathname.split("/").pop();
    return parseInt(id, 10);
  }, [location.pathname]);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [personToAssign, setPersonToAssign] = useState(null);
  
  // On regroupe tous les paramètres de la requête dans un seul état
  const [queryParams, setQueryParams] = useState({
    typeId: 1,
    page: 1,
    limit: 10,
    search: "",
    governmentId: null,
    regionId: null,
    specialtyId: null,
  });

  // --- Chargement des données pour les filtres ---
  useEffect(() => {
    dispatch(getGovernments({ page: 1, limit: 100 }));
    dispatch(getSpecialtysList({ page: 1, itemsPerPage: 100, filter: { search: "" } }));
  }, [dispatch]);

  useEffect(() => {
    // On charge les régions seulement si un gouvernement est sélectionné
    if (queryParams.governmentId) {
      dispatch(getRegions({ page: 1, itemsPerPage: 100, governmentId: queryParams.governmentId }));
    }
  }, [queryParams.governmentId, dispatch]);

  // --- Appel API principal ---
  useEffect(() => {
    // On ne fait l'appel que si l'ID du délégué est un nombre valide
    if (!isNaN(delegateId)) {
      dispatch(getNotAssignedPersons({ delegateId, ...queryParams }));
    }
  }, [dispatch, delegateId, queryParams]);

  // --- Gestionnaires d'événements ---
  const navigateTo = (path) => history.push(path);

  const handlePageChange = (page, pageSize) => {
    setQueryParams(prev => ({ ...prev, page, limit: pageSize }));
  };

  const handleFilterChange = (value, key) => {
    // CORRECTION : On n'ajoute plus "Id" à la clé. 
    // La clé venant de `optionsLists` est déjà correcte (ex: "regionId").
    setQueryParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = (value) => {
    setQueryParams(prev => ({ ...prev, search: value, page: 1 }));
  };
  
  const handleAssignMultiple = () => {
    if (selectedRowKeys.length > 0) {
      dispatch(assignPersons({ id: delegateId, persons: selectedRowKeys })).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Personnes assignées avec succès !");
          history.push(`/assigné/délégué/${delegateId}`);
        }
      });
    }
  };
  
  const handleAssignSingle = () => {
    if (personToAssign) {
      dispatch(assignPersons({ id: delegateId, persons: [personToAssign] })).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Personne assignée avec succès !");
          setIsModalVisible(false);
          setPersonToAssign(null);
          // Rafraîchir la liste en relançant le useEffect
          setQueryParams(prev => ({...prev}));
        }
      });
    }
  };

  const showAssignModal = (personId) => {
    setPersonToAssign(personId);
    setIsModalVisible(true);
  };
  
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // --- Création des options pour les filtres ---
  const createOptions = (data, labelKey = "name", valueKey = "id") => {
    return data?.data?.map(item => ({ label: item[labelKey], value: item[valueKey] })) || [];
  };

  const optionsLists = useMemo(() => [
    { name: "Type", placeholder: "Type", key: "typeId", options: [ { label: "Médecin", value: 1 }, { label: "Pharmacien", value: 2 }, { label: "Grossiste", value: 3 }]},
    { name: "Spécialités", placeholder: "Spécialités", key: "specialtyId", multiple: true, isLoading: sepcialty_loading, options: createOptions(sepcialtysData) },
    { name: "Gouvernorat", placeholder: "Gouvernorat", key: "governmentId", multiple: true, isLoading: government_loading, options: createOptions(governmentsData) },
    { name: "Région", placeholder: "Région", key: "regionId", multiple: true, isLoading: region_loading, options: createOptions(regions_data) },
  ], [sepcialtysData, governmentsData, regions_data, sepcialty_loading, government_loading, region_loading]);
  
  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <>
                <Button type="link" onClick={() => navigateTo(`/assigné/délégué/${delegateId}`)} style={{ marginRight: "16px" }}>
                  <ArrowLeftOutlined /> Retour à la liste assignée
                </Button>
                Assigner de nouvelles personnes
              </>
            }
          />
          <Card bordered={false} className="criclebox tablespace mb-24">
            <Card bordered={false} className="criclebox tablespace mb-24" title={
              <>
                <Filter
                  searchPlaceholder="Rechercher par nom, code, email..."
                  optionsLists={optionsLists}
                  onSelect={handleFilterChange}
                  onSearchValue={handleSearch}
                  data={queryParams}
                />
                <Button
                  disabled={selectedRowKeys.length === 0}
                  style={{ marginTop: "16px" }}
                  type="primary"
                  onClick={handleAssignMultiple}
                  loading={assignment_loading}
                >
                  Assigner la sélection ({selectedRowKeys.length})
                </Button>
              </>
            }>
            </Card>
            <Table
              rowSelection={rowSelection}
              columns={getColumnsByType([queryParams.typeId], navigateTo, showAssignModal)}
              dataSource={persons_data?.data}
              loading={persons_loading}
              className="ant-border-space"
              pagination={{
                current: queryParams.page,
                pageSize: queryParams.limit,
                total: persons_data?.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
              }}
              scroll={{ x: true }}
              rowKey="id"
            />
          </Card>
        </Col>
        <Modal
          title="Confirmer l'assignation"
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="back" onClick={() => setIsModalVisible(false)}>Annuler</Button>,
            <Button key="submit" type="primary" loading={assignment_loading} onClick={handleAssignSingle}>Confirmer</Button>,
          ]}
        >
          <p>Êtes-vous sûr de vouloir assigner ce praticien à ce délégué ?</p>
        </Modal>
      </Row>
    </div>
  );
};

export default AssignPersonsPage;